import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { LEGAL_STATUSES, LEGAL_TYPES, operationsApi } from 'features/operations/operations.api';
import { listItems, listMeta } from 'features/operations/operations.helpers';
import { Badge, EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { formatDateTime } from 'shared/utilities/format';

export function LegalDocumentsPage() {
  const { t, locale, dir } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.legalDocumentsManage);
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => ({
    page: Number(searchParams.get('page') || 1),
    per_page: 20,
    type: searchParams.get('type') || '',
    status: searchParams.get('status') || '',
  }), [searchParams]);

  const query = useQuery({
    queryKey: queryKeys.legalDocuments(filters),
    queryFn: ({ signal }) => operationsApi.listLegalDocuments(filters, { signal }),
  });

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.set('page', '1');
    setSearchParams(next);
  };

  const items = listItems(query.data);
  const meta = listMeta(query.data);

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader
        title={t('legal.title')}
        actions={canManage ? <Link className="btn btn-primary" to={paths.legalDocumentCreate}>{t('legal.create')}</Link> : null}
      />
      <div className="filter-grid">
        <select className="input" value={filters.type} onChange={(event) => setFilter('type', event.target.value)} aria-label={t('legal.type')}>
          <option value="">{t('filters.all')}</option>
          {LEGAL_TYPES.map((type) => <option key={type} value={type}>{t(`legal.types.${type}`)}</option>)}
        </select>
        <select className="input" value={filters.status} onChange={(event) => setFilter('status', event.target.value)} aria-label={t('legal.status')}>
          <option value="">{t('filters.all')}</option>
          {LEGAL_STATUSES.map((status) => <option key={status} value={status}>{t(`legal.statuses.${status}`)}</option>)}
        </select>
        <button type="button" className="btn btn-secondary" onClick={() => setSearchParams({})}>{t('actions.reset')}</button>
      </div>
      {items.length === 0 ? <EmptyState title={t('empty.legalDocuments')} /> : (
        <div className="table-wrap">
        <table className="data-table has-sticky-actions">
          <thead>
            <tr>
              <th>{t('legal.type')}</th>
              <th>{t('legal.version')}</th>
              <th>{t('legal.titleEn')}</th>
              <th>{t('legal.status')}</th>
              <th>{t('legal.current')}</th>
              <th>{t('legal.effectiveAt')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((document) => (
              <tr key={document.id}>
                <td>{t(`legal.types.${document.type}`)}</td>
                <td>{document.version}</td>
                <td>{locale === 'ar' ? document.title_ar : document.title_en}</td>
                <td><Badge>{t(`legal.statuses.${document.status}`)}</Badge></td>
                <td>{document.is_current ? t('legal.current') : '—'}</td>
                <td>{formatDateTime(document.effective_at, locale)}</td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${document.id}`}
                  items={[detailsAction(paths.legalDocumentDetail(document.id), t)]}
                />
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilter('page', String(page))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}
